<template>
  <a-card title="构图作品列表">
    <a-form layout="inline" :model="filters" class="search-form" @finish="search">
      <a-form-item label="任务 ID">
        <a-input v-model:value="filters.task_id" allow-clear placeholder="请输入任务 ID" />
      </a-form-item>
      <a-form-item label="用户 ID">
        <a-input v-model:value="filters.user_id" allow-clear placeholder="请输入用户 ID" />
      </a-form-item>
      <a-form-item label="状态">
        <a-select v-model:value="filters.status" allow-clear placeholder="全部状态" :options="statusOptions" class="status-select" />
      </a-form-item>
      <a-form-item label="创建时间">
        <a-range-picker
          v-model:value="filters.createdRange"
          show-time
          value-format="YYYY-MM-DD HH:mm:ss"
          format="YYYY-MM-DD HH:mm"
        />
      </a-form-item>
      <a-form-item>
        <a-space>
          <a-button type="primary" html-type="submit">查询</a-button>
          <a-button @click="resetFilters">重置</a-button>
        </a-space>
      </a-form-item>
    </a-form>

    <a-table
      row-key="id"
      :columns="columns"
      :data-source="records"
      :loading="loading"
      :pagination="pagination"
      :scroll="{ x: 1380 }"
      @change="handleTableChange"
    >
      <template #task_id="{ record }">
        <div class="task-id">{{ record.task_id }}</div>
        <div class="muted">记录 #{{ record.id }}</div>
      </template>
      <template #user_id="{ record }">
        <a @click="filters.user_id = String(record.user_id); search()">{{ record.user_id || '-' }}</a>
      </template>
      <template #source_image="{ record }">
        <a-image v-if="record.source_object_key" :src="thumbnail(record.source_object_key)" :preview="{ src: previewImage(record.source_object_key) }" class="work-image" />
        <span v-else class="muted">无原图</span>
      </template>
      <template #composed_image="{ record }">
        <a-image v-if="record.object_key" :src="thumbnail(record.object_key)" :preview="{ src: previewImage(record.object_key) }" class="work-image" />
        <span v-else class="muted">-</span>
      </template>
      <template #final_image="{ record }">
        <a-image v-if="record.final_image_url" :src="thumbnail(record.final_image_url)" :preview="{ src: previewImage(record.final_image_url) }" class="work-image" />
        <span v-else class="muted">-</span>
      </template>
      <template #status="{ record }">
        <a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag>
        <a-tooltip v-if="record.error_message" :title="record.error_message">
          <a-tag color="error">{{ record.error_code || '错误详情' }}</a-tag>
        </a-tooltip>
      </template>
      <template #filters="{ record }">
        <div>推荐：{{ record.recommended_filter_id || '-' }}</div>
        <div>应用：{{ record.applied_filter_id || '-' }}</div>
      </template>
      <template #created_at="{ record }">{{ formatTime(record.created_at) }}</template>
      <template #received_at="{ record }">
        <div>构图：{{ formatTime(record.received_at) }}</div>
        <div>成片：{{ formatTime(record.final_received_at) }}</div>
      </template>
    </a-table>
  </a-card>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import request from '@/utils/request'
import { previewImage, thumbnail } from '@/utils/image'

type CompositionTask = {
  id: number
  task_id: string
  user_id: number
  source_object_key: string
  object_key: string
  final_image_url: string
  recommended_filter_id: string
  applied_filter_id: string
  error_code: string
  error_message: string
  status: string
  created_at: string
  received_at?: string
  final_received_at?: string
}

const loading = ref(false)
const records = ref<CompositionTask[]>([])
const filters = reactive({ task_id: '', user_id: '', status: undefined as string | undefined, createdRange: null as string[] | null })
const pagination = reactive({ total: 0, current: 1, pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'], showTotal: (total: number) => `共 ${total} 条` })
const statusOptions = [
  { label: '待构图回传', value: 'receive_result' },
  { label: '已构图', value: 'composed' },
  { label: '已完成', value: 'completed' },
  { label: '失败', value: 'failed' },
]
const columns = [
  { title: '任务 ID', key: 'task_id', slots: { customRender: 'task_id' }, width: 220 },
  { title: '用户 ID', key: 'user_id', slots: { customRender: 'user_id' }, width: 100 },
  { title: '原始照片', key: 'source_image', slots: { customRender: 'source_image' }, width: 150 },
  { title: '构图结果', key: 'composed_image', slots: { customRender: 'composed_image' }, width: 150 },
  { title: '最终作品', key: 'final_image', slots: { customRender: 'final_image' }, width: 150 },
  { title: '状态', key: 'status', slots: { customRender: 'status' }, width: 130 },
  { title: '滤镜', key: 'filters', slots: { customRender: 'filters' }, width: 180 },
  { title: '回传时间', key: 'received_at', slots: { customRender: 'received_at' }, width: 180 },
  { title: '创建时间', key: 'created_at', slots: { customRender: 'created_at' }, width: 160 },
]

async function loadRecords(page = pagination.current, pageSize = pagination.pageSize) {
  loading.value = true
  try {
    const params: Record<string, unknown> = {
      offset: (page - 1) * pageSize,
      limit: pageSize,
      task_id: filters.task_id.trim() || undefined,
      user_id: filters.user_id.trim() || undefined,
      status: filters.status || undefined,
      start_at: filters.createdRange?.[0] || undefined,
      end_at: filters.createdRange?.[1] || undefined,
    }
    const response: any = await request.get('/api/composition/tasks', params)
    records.value = response?.data?.list || []
    pagination.total = response?.data?.pagination?.total || 0
    pagination.current = page
    pagination.pageSize = pageSize
  } finally {
    loading.value = false
  }
}

function search() {
  loadRecords(1, pagination.pageSize)
}

function resetFilters() {
  filters.task_id = ''
  filters.user_id = ''
  filters.status = undefined
  filters.createdRange = null
  search()
}

function handleTableChange(pager: any) {
  loadRecords(pager.current, pager.pageSize)
}

function formatTime(value?: string) {
  if (!value) return '-'
  return value.replace('T', ' ').replace(/\.\d+(Z|[+-]\d{2}:?\d{2})?$/, '').replace(/Z$/, '')
}

function statusLabel(status: string) {
  return ({ receive_result: '待构图回传', composed: '已构图', completed: '已完成', failed: '失败' } as Record<string, string>)[status] || status || '-'
}

function statusColor(status: string) {
  return ({ receive_result: 'processing', composed: 'blue', completed: 'success', failed: 'error' } as Record<string, string>)[status] || 'default'
}

onMounted(() => loadRecords())
</script>

<style scoped>
.search-form { row-gap: 12px; margin-bottom: 20px; }
.status-select { width: 145px; }
.task-id { max-width: 210px; overflow-wrap: anywhere; }
.muted { color: rgba(0, 0, 0, 0.45); font-size: 12px; }
.work-image { width: 112px; max-height: 145px; object-fit: contain; }
</style>
